import type { TFunction } from 'i18next';
import type { Account, AccountNode } from '../../types/bookkeeping.js';

/** Flatten a nested account tree into a parent-before-descendant list. */
export function flattenTree(nodes: AccountNode[]): Account[] {
  const out: Account[] = [];
  const walk = (ns: AccountNode[]) => {
    for (const n of ns) {
      out.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** Format a signed minor-units (cents) string as an exact two-decimal string. */
export function fmtMoney(cents: string): string {
  const n = BigInt(cents || '0');
  const neg = n < 0n;
  const abs = neg ? -n : n;
  const whole = abs / 100n;
  const frac = (abs % 100n).toString().padStart(2, '0');
  return `${neg ? '-' : ''}${whole}.${frac}`;
}

/**
 * Maps an API error code to friendly banking copy, falling back to the raw code
 * so an unmapped code is still visible rather than silently blank.
 *
 * Call sites pass `err.body?.error`, which for a zod-validator 400 response is
 * a ZodError object rather than a string. Guard against that here so a
 * non-string body can never reach i18next's `defaultValue` (which would try
 * to render the object as a React child and crash) — fall back to the
 * generic `banking.err.error` copy instead.
 */
export function bankingErrorMessage(t: TFunction, code?: unknown): string {
  const key = typeof code === 'string' && code.length > 0 ? code : 'error';
  return t(`banking.err.${key}`, { defaultValue: key });
}
